import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { jobsAPI, employeesAPI, timeOffAPI, timeTrackingAPI, availabilityAPI } from '../lib/api';
import { hasAdminUI } from '../lib/permissions';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Clock,
  MapPin,
  Building2,
  Briefcase,
  Download,
  Calendar as CalendarIcon,
  Users,
  Palmtree,
  CheckCircle,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  parseISO,
  isWithinInterval,
  isBefore,
  isAfter,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { toast } from 'sonner';

const jobStatusColors = {
  pending: { bg: 'bg-yellow-500', light: 'bg-yellow-200 text-yellow-900', border: 'border-yellow-500' },
  in_progress: { bg: 'bg-blue-500', light: 'bg-blue-200 text-blue-900', border: 'border-blue-500' },
  completed: { bg: 'bg-emerald-500', light: 'bg-emerald-200 text-emerald-900', border: 'border-emerald-500' },
  cancelled: { bg: 'bg-red-500', light: 'bg-red-200 text-red-900', border: 'border-red-500' },
};

// Outlook-style colors for employees
const employeeColors = [
  { bg: 'bg-purple-500', light: 'bg-purple-200 text-purple-900', border: 'border-purple-500' },
  { bg: 'bg-blue-500', light: 'bg-blue-200 text-blue-900', border: 'border-blue-500' },
  { bg: 'bg-emerald-500', light: 'bg-emerald-200 text-emerald-900', border: 'border-emerald-500' },
  { bg: 'bg-orange-500', light: 'bg-orange-200 text-orange-900', border: 'border-orange-500' },
  { bg: 'bg-pink-500', light: 'bg-pink-200 text-pink-900', border: 'border-pink-500' },
  { bg: 'bg-cyan-500', light: 'bg-cyan-200 text-cyan-900', border: 'border-cyan-500' },
  { bg: 'bg-indigo-500', light: 'bg-indigo-200 text-indigo-900', border: 'border-indigo-500' },
  { bg: 'bg-rose-500', light: 'bg-rose-200 text-rose-900', border: 'border-rose-500' },
];

export default function Schedule() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const isAdminUser = hasAdminUI(user);
  const [calendarView, setCalendarView] = useState(isAdminUser ? 'week' : 'month'); // Employees: month-only
  const [viewMode, setViewMode] = useState(isAdminUser ? 'personnel' : 'jobs'); // Employees: jobs-only
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]); // Time tracking entries
  const [employeeAvailability, setEmployeeAvailability] = useState([]); // All employees' availability
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [selectedCostCenter, setSelectedCostCenter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const [showSyncDialog, setShowSyncDialog] = useState(false);
  const [calendarFeedUrl, setCalendarFeedUrl] = useState('');
  const [settingAvailability, setSettingAvailability] = useState(false);
  const isAdmin = isAdminUser;

  // Filter employees to exclude admins and inactive employees for the Week view
  const nonAdminEmployees = employees.filter(emp => emp.role !== 'admin' && emp.is_active !== false);

  // Get unique cost centers from both jobs AND active employees (support both single and multiple cost centers)
  const jobCostCenters = jobs.filter(j => j.cost_center).map(j => j.cost_center);
  const activeEmployees = useMemo(() => employees.filter(e => e.is_active !== false), [employees]);
  const employeeCostCenters = activeEmployees.flatMap(e => 
    e.cost_centers && e.cost_centers.length > 0 
      ? e.cost_centers 
      : (e.cost_center ? [e.cost_center] : [])
  );
  const costCenters = [...new Set([...jobCostCenters, ...employeeCostCenters])].filter(Boolean).sort();

  // Filter jobs by cost center (matches job's cost_center OR assigned active employee's cost_centers)
  const filteredJobs = selectedCostCenter === 'all' 
    ? jobs 
    : jobs.filter(job => {
        // Match if job has the selected cost center
        if (job.cost_center === selectedCostCenter) return true;
        // Match if any assigned active employee has the selected cost center (support both array and single value)
        const assignedEmps = activeEmployees.filter(e => job.assigned_to?.includes(e.id));
        return assignedEmps.some(e => 
          (e.cost_centers && e.cost_centers.includes(selectedCostCenter)) || 
          e.cost_center === selectedCostCenter
        );
      });

  // Filter approved time off requests
  const approvedTimeOff = timeOffRequests.filter(t => t.status === 'approved');

  // Check if an employee has time off on a specific day
  const getTimeOffForDay = (employeeId, day) => {
    return approvedTimeOff.filter(t => {
      if (t.user_id !== employeeId) return false;
      const startDate = parseISO(t.start_date);
      const endDate = parseISO(t.end_date);
      return isWithinInterval(day, { start: startOfDay(startDate), end: endOfDay(endDate) });
    });
  };

  // Check if any employee has time off on a specific day (for monthly view)
  const getTimeOffForDayAll = (day) => {
    return approvedTimeOff.filter(t => {
      const startDate = parseISO(t.start_date);
      const endDate = parseISO(t.end_date);
      return isWithinInterval(day, { start: startOfDay(startDate), end: endOfDay(endDate) });
    });
  };

  // Get time entries for a specific day (for Personnel view)
  const getTimeEntriesForDay = (day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return timeEntries.filter(entry => entry.date === dayStr);
  };

  // Get time entries for a specific employee on a specific day
  const getTimeEntriesForEmployeeDay = (employeeId, day) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    return timeEntries.filter(entry => entry.employee_id === employeeId && entry.date === dayStr);
  };

  // Get employee availability for a specific day (0=Sunday, 1=Monday, etc.)
  const getEmployeeAvailabilityForDay = (employeeId, day) => {
    const dayOfWeek = day.getDay(); // 0=Sunday, 1=Monday, etc.
    return employeeAvailability.find(
      a => a.user_id === employeeId && a.day_of_week === dayOfWeek
    );
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const start = calendarView === 'month' 
        ? startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 })
        : startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = calendarView === 'month'
        ? endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 })
        : endOfWeek(currentDate, { weekStartsOn: 0 });
      
      const [jobsRes, employeesRes, timeOffRes, timeEntriesRes] = await Promise.all([
        jobsAPI.getAll({
          start_date: format(start, "yyyy-MM-dd'T'00:00:00"),
          end_date: format(end, "yyyy-MM-dd'T'23:59:59"),
        }),
        employeesAPI.getAll({ page_size: 1000 }),
        timeOffAPI.getAll(),
        timeTrackingAPI.getEntries({
          start_date: format(start, 'yyyy-MM-dd'),
          end_date: format(end, 'yyyy-MM-dd'),
        }).catch(() => ({ data: { entries: [] } })), // Graceful fallback
      ]);
      
      setJobs(jobsRes.data);
      setEmployees(employeesRes.data);
      setTimeOffRequests(timeOffRes.data || []);
      setTimeEntries(timeEntriesRes.data?.entries || []);
      
      // Fetch availability for all employees (admin only, Week view)
      if (isAdmin) {
        try {
          const availabilityRes = await availabilityAPI.getAll();
          setEmployeeAvailability(availabilityRes.data?.availability || []);
        } catch (e) {
          console.log('Could not fetch availability:', e);
          setEmployeeAvailability([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentDate, calendarView, isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = async () => {
    try {
      const start = calendarView === 'month' 
        ? startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 })
        : startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = calendarView === 'month'
        ? endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 })
        : endOfWeek(currentDate, { weekStartsOn: 0 });

      await jobsAPI.exportCSV({
        start_date: format(start, "yyyy-MM-dd'T'00:00:00"),
        end_date: format(end, "yyyy-MM-dd'T'23:59:59"),
      });
      toast.success('Schedule exported successfully');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleShowSync = () => {
    setCalendarFeedUrl(jobsAPI.getCalendarFeedUrl());
    setShowSyncDialog(true);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(calendarFeedUrl);
    toast.success('Calendar URL copied to clipboard');
  };

  // Get color for employee based on their index
  const getEmployeeColor = (employeeId) => {
    const index = employees.findIndex(e => e.id === employeeId);
    if (index === -1) return employeeColors[0]; // Fallback for unknown employees
    return employeeColors[index % employeeColors.length];
  };

  // Check if a job spans across a specific day
  const isJobOnDay = (job, day) => {
    const jobStart = startOfDay(parseISO(job.start_time));
    const jobEnd = endOfDay(parseISO(job.end_time));
    const dayStart = startOfDay(day);
    const dayEnd = endOfDay(day);
    
    return isWithinInterval(dayStart, { start: jobStart, end: jobEnd }) ||
           isWithinInterval(dayEnd, { start: jobStart, end: jobEnd }) ||
           (isBefore(jobStart, dayStart) && isAfter(jobEnd, dayEnd));
  };

  // Check if job starts on this day
  const isJobStartDay = (job, day) => {
    return isSameDay(parseISO(job.start_time), day);
  };

  // Check if job ends on this day
  const isJobEndDay = (job, day) => {
    return isSameDay(parseISO(job.end_time), day);
  };

  // Get jobs that span across a specific day (uses filtered jobs)
  const getJobsForDay = (day) => {
    return filteredJobs.filter(job => isJobOnDay(job, day));
  };

  // Get position in week (0-6) for a day
  const getDayOfWeek = (day) => {
    return day.getDay();
  };

  // Calculate how many days until end of week or job end
  const getSpanDays = (job, day, weekDays) => {
    const jobEnd = parseISO(job.end_time);
    const dayIndex = weekDays.findIndex(d => isSameDay(d, day));
    let span = 1;
    
    for (let i = dayIndex + 1; i < weekDays.length; i++) {
      if (isBefore(weekDays[i], jobEnd) || isSameDay(weekDays[i], jobEnd)) {
        span++;
      } else {
        break;
      }
    }
    return span;
  };

  // Bulk set availability for all employees
  const handleBulkSetAvailability = async () => {
    if (!window.confirm('This will set all employees to 24/7 availability (07:00 - 07:00, Mon-Sun). Continue?')) {
      return;
    }
    
    setSettingAvailability(true);
    try {
      const res = await availabilityAPI.bulkSetAll();
      toast.success(`Set availability for ${res.data.employees_updated} employees`);
      // Refresh availability data
      const availabilityRes = await availabilityAPI.getAll();
      setEmployeeAvailability(availabilityRes.data?.availability || []);
    } catch (error) {
      console.error('Failed to set availability:', error);
      toast.error('Failed to set availability for all employees');
    } finally {
      setSettingAvailability(false);
    }
  };

  const handlePrev = () => {
    if (calendarView === 'month') {
      setCurrentDate(subMonths(currentDate, 1));
    } else {
      setCurrentDate(subWeeks(currentDate, 1));
    }
  };

  const handleNext = () => {
    if (calendarView === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addWeeks(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(currentDate, { weekStartsOn: 0 });
  const weekDays = eachDayOfInterval({ start: weekStart, end: weekEnd });

  const shortDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Group calendar days into weeks
  const weeks = [];
  for (let i = 0; i < calendarDays.length; i += 7) {
    weeks.push(calendarDays.slice(i, i + 7));
  }

  // Get spanning events for a week
  const getWeekEvents = (weekDays) => {
    const events = [];
    const processedJobs = new Set();

    weekDays.forEach((day, dayIndex) => {
      const dayJobs = getJobsForDay(day);
      
      dayJobs.forEach(job => {
        if (processedJobs.has(job.id)) return;
        
        const jobStart = parseISO(job.start_time);
        const jobEnd = parseISO(job.end_time);
        
        // Only start rendering from the first day of this week that the job appears
        const startsThisWeek = weekDays.some(d => isSameDay(d, jobStart));
        const firstDayInWeek = startsThisWeek ? 
          weekDays.findIndex(d => isSameDay(d, jobStart)) : 
          0;
        
        if (dayIndex === firstDayInWeek || (!startsThisWeek && dayIndex === 0 && isJobOnDay(job, day))) {
          // Calculate span
          let span = 0;
          for (let i = dayIndex; i < 7; i++) {
            if (isJobOnDay(job, weekDays[i])) {
              span++;
            } else {
              break;
            }
          }
          
          if (span > 0) {
            // For personnel view, create event for each assigned employee
            if (viewMode === 'personnel') {
              (job.assigned_to || []).forEach((empId, idx) => {
                if (selectedEmployee === 'all' || selectedEmployee === empId) {
                  events.push({
                    job,
                    employeeId: empId,
                    employeeName: job.assigned_names?.[idx] || 'Unknown',
                    startDayIndex: dayIndex,
                    span,
                    isStart: isSameDay(jobStart, weekDays[dayIndex]),
                    isEnd: weekDays[dayIndex + span - 1] && isSameDay(jobEnd, weekDays[dayIndex + span - 1]),
                  });
                }
              });
            } else {
              events.push({
                job,
                startDayIndex: dayIndex,
                span,
                isStart: isSameDay(jobStart, weekDays[dayIndex]),
                isEnd: weekDays[dayIndex + span - 1] && isSameDay(jobEnd, weekDays[dayIndex + span - 1]),
              });
            }
            processedJobs.add(job.id);
          }
        }
      });
    });

    return events;
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in bg-slate-100 dark:bg-slate-200 -m-4 sm:-m-6 p-4 sm:p-6 min-h-screen" data-testid="schedule-page">
      {/* Header */}
      <div className="calendar-header">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800">Schedule</h1>
          <p className="text-slate-600 mt-1 text-sm sm:text-base">
            {calendarView === 'month' 
              ? format(currentDate, 'MMMM yyyy')
              : `Week of ${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
            }
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* View Mode Toggle - Personnel first (admin only) */}
          {isAdmin && (
            <div className="flex items-center gap-1 sm:gap-2 p-1 bg-muted rounded-lg">
              <Button
                variant={viewMode === 'personnel' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('personnel')}
                data-testid="toggle-personnel"
                className="px-2 sm:px-3"
              >
                <Users className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Personnel</span>
              </Button>
              <Button
                variant={viewMode === 'jobs' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('jobs')}
                data-testid="toggle-jobs"
                className="px-2 sm:px-3"
              >
                <Briefcase className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Jobs</span>
              </Button>
            </div>
          )}

          {/* Calendar View Toggle - After Jobs/Personnel (Week is admin-only) */}
          <Tabs value={calendarView} onValueChange={setCalendarView}>
            <TabsList className="h-9 bg-slate-100">
              <TabsTrigger value="month" data-testid="month-view-btn" className="text-xs sm:text-sm px-2 sm:px-3">Month</TabsTrigger>
              {isAdmin && (
                <TabsTrigger value="week" data-testid="week-view-btn" className="text-xs sm:text-sm px-2 sm:px-3">Week</TabsTrigger>
              )}
            </TabsList>
          </Tabs>

          {/* Employee Filter - admin only (Personnel mode or Weekly view) */}
          {isAdmin && (viewMode === 'personnel' || calendarView === 'week') && (
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-32 sm:w-48 bg-slate-700 text-white border-slate-600 hover:bg-slate-600" data-testid="employee-filter">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {activeEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Cost Center (Division) Filter - admin only */}
          {isAdmin && (
            <Select value={selectedCostCenter} onValueChange={setSelectedCostCenter}>
              <SelectTrigger className="w-28 sm:w-40 bg-slate-700 text-white border-slate-600 hover:bg-slate-600" data-testid="cost-center-filter">
                <SelectValue placeholder="All Divisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {costCenters.map((cc) => (
                  <SelectItem key={cc} value={cc}>
                    {cc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Export & Sync - Darker styling (Export admin only) */}
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleExportCSV} 
                title="Export CSV" 
                data-testid="export-csv-btn" 
                className="px-2 sm:px-3 bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
              >
                <Download className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleShowSync} 
              title="Sync Calendar" 
              data-testid="sync-calendar-btn" 
              className="px-2 sm:px-3 bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
            >
              <CalendarIcon className="w-4 h-4 sm:mr-2 text-muted-foreground" />
              <span className="hidden sm:inline">Sync</span>
            </Button>
          </div>

          {/* Navigation - Darker styling for all buttons */}
          <div className="flex items-center gap-1">
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handlePrev} 
              data-testid="prev-btn" 
              className="h-9 w-9 bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleToday} 
              data-testid="today-btn" 
              className="h-9 px-2 sm:px-3 text-xs sm:text-sm bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
            >
              Today
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleNext} 
              data-testid="next-btn" 
              className="h-9 w-9 bg-slate-700 text-white border-slate-600 hover:bg-slate-600 hover:text-white"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Add Job Button */}
          {isAdmin && (
            <Button onClick={() => navigate('/jobs/new')} data-testid="create-job-btn" size="sm" className="h-9">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Job</span>
            </Button>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <Card className="overflow-hidden bg-white dark:bg-white border-slate-300 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : calendarView === 'week' ? (
            /* Weekly Grid View - Employee rows x Day columns */
            <div data-testid="weekly-grid-view" className="overflow-x-auto">
              {/* Header row with days */}
              <div className="grid border-b border-slate-200 bg-slate-50" style={{ gridTemplateColumns: '140px repeat(7, minmax(100px, 1fr))' }}>
                <div className="px-2 py-2 sm:py-3 text-xs sm:text-sm font-semibold text-slate-600 border-r border-slate-200">
                  Employee
                </div>
                {weekDays.map((day) => {
                  const isToday = isSameDay(day, new Date());
                  return (
                    <div
                      key={day.toISOString()}
                      className={`px-1 sm:px-2 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold border-l border-slate-200 first:border-l-0 ${
                        isToday ? 'bg-blue-50 text-blue-600' : 'text-slate-600'
                      }`}
                    >
                      <div>{format(day, 'EEE')}</div>
                      <div className={`text-base sm:text-lg ${isToday ? 'font-bold' : ''}`}>{format(day, 'd')}</div>
                    </div>
                  );
                })}
              </div>

              {/* Employee rows */}
              {nonAdminEmployees.length === 0 ? (
                <div className="p-6 sm:p-8 text-center text-slate-500 text-sm">
                  No employees found
                </div>
              ) : (
                nonAdminEmployees
                  .filter(emp => selectedEmployee === 'all' || selectedEmployee === emp.id)
                  .filter(emp => selectedCostCenter === 'all' || 
                    (emp.cost_centers && emp.cost_centers.includes(selectedCostCenter)) || 
                    emp.cost_center === selectedCostCenter)  // Backwards compatibility
                  .map((employee, empIndex) => {
                    const employeeColor = getEmployeeColor(employee.id);
                    return (
                      <div
                        key={employee.id}
                        className={`grid border-b border-slate-200 last:border-b-0 ${empIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                        style={{ gridTemplateColumns: '140px repeat(7, minmax(100px, 1fr))' }}
                        data-testid={`employee-row-${employee.id}`}
                      >
                        {/* Employee name cell */}
                        <div className="px-2 py-2 border-r border-slate-200 flex items-center min-h-[50px] sm:min-h-[60px] bg-slate-50">
                          <span className="font-medium text-xs sm:text-sm truncate text-slate-700">{employee.name}</span>
                        </div>

                        {/* Day cells */}
                        {weekDays.map((day) => {
                          const isToday = isSameDay(day, new Date());
                          // Get jobs for this employee on this day (uses filtered jobs)
                          const employeeJobsForDay = filteredJobs.filter(job => 
                            job.assigned_to?.includes(employee.id) && isJobOnDay(job, day)
                          );
                          // Get time off for this employee on this day
                          const employeeTimeOff = getTimeOffForDay(employee.id, day);
                          const hasTimeOff = employeeTimeOff.length > 0;
                          
                          // Get time entries for this employee on this day
                          const employeeTimeEntries = getTimeEntriesForEmployeeDay(employee.id, day);
                          
                          // Get availability for this employee on this day
                          const availability = getEmployeeAvailabilityForDay(employee.id, day);

                          return (
                            <div
                              key={day.toISOString()}
                              className={`px-1 py-1 border-l border-slate-200 min-h-[50px] sm:min-h-[60px] ${
                                isToday ? 'bg-blue-50/50' : ''
                              } ${hasTimeOff ? 'bg-purple-50' : ''}`}
                              onClick={() => setSelectedDate(day)}
                              data-testid={`cell-${employee.id}-${format(day, 'yyyy-MM-dd')}`}
                            >
                              {/* Time Off Badge */}
                              {hasTimeOff && (
                                <div className="bg-purple-100 text-purple-700 rounded px-1 sm:px-1.5 py-0.5 sm:py-1 text-[10px] sm:text-xs mb-1 flex items-center gap-1">
                                  <Palmtree className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                  <span className="font-medium hidden sm:inline">Time Off</span>
                                  <span className="font-medium sm:hidden">Off</span>
                                </div>
                              )}
                              {/* Show time entries if available, otherwise show job assignments */}
                              {employeeTimeEntries.length > 0 ? (
                                <div className="space-y-1">
                                  {employeeTimeEntries.map((entry) => {
                                    return (
                                      <div
                                        key={entry.id}
                                        className={`${employeeColor.light} rounded px-1 sm:px-1.5 py-0.5 sm:py-1 text-[10px] sm:text-xs cursor-pointer hover:opacity-80 transition-opacity`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const job = jobs.find(j => j.id === entry.job_id);
                                          if (job) setSelectedJob(job);
                                        }}
                                        data-testid={`weekly-entry-${entry.id}`}
                                        title={`${entry.job_title}\n${entry.start_time} - ${entry.end_time}\n${entry.hours}h logged`}
                                      >
                                        <div className="font-medium truncate">{entry.job_title}</div>
                                        <div className="text-[8px] sm:text-[10px] opacity-75 flex items-center gap-1">
                                          <Clock className="w-2 h-2 text-muted-foreground" />
                                          {entry.hours}h
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : employeeJobsForDay.length === 0 && !hasTimeOff ? (
                                // No jobs scheduled - show availability status
                                <div className="h-full flex flex-col items-center justify-center">
                                  {availability ? (
                                    availability.is_available ? (
                                      <div className="bg-green-100 text-green-700 rounded px-1.5 py-1 text-[10px] sm:text-xs text-center">
                                        <div className="font-medium">Available</div>
                                      </div>
                                    ) : (
                                      <div className="bg-gray-200 text-gray-600 rounded px-1.5 py-1 text-[10px] sm:text-xs text-center">
                                        <div className="font-medium">Not Available</div>
                                      </div>
                                    )
                                  ) : (
                                    <div className="text-[10px] sm:text-xs text-slate-400">
                                      —
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="space-y-1">
                                  {employeeJobsForDay.map((job) => {
                                    // Use employee color in personnel mode, job status color in jobs mode
                                    const colors = viewMode === 'personnel'
                                      ? employeeColor
                                      : jobStatusColors[job.status] || jobStatusColors.pending;
                                    return (
                                      <div
                                        key={job.id}
                                        className={`${colors.light} rounded px-1 sm:px-1.5 py-0.5 sm:py-1 text-[10px] sm:text-xs cursor-pointer hover:opacity-80 transition-opacity opacity-60`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedJob(job);
                                        }}
                                        data-testid={`weekly-job-${job.id}`}
                                        title={`${job.title}\n${format(parseISO(job.start_time), 'h:mm a')} - ${format(parseISO(job.end_time), 'h:mm a')}\n(No time logged)`}
                                      >
                                        <div className="font-medium truncate">{job.title}</div>
                                        <div className="text-[8px] sm:text-[10px] opacity-75">
                                          {format(parseISO(job.start_time), 'h:mma')}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
              )}
            </div>
          ) : (
            /* Monthly Calendar View */
            <div data-testid="monthly-calendar-view" className="overflow-x-auto">
              {/* Day headers */}
              <div className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))' }}>
                {shortDayNames.map((day, idx) => (
                  <div
                    key={day}
                    className="px-1 sm:px-2 py-2 sm:py-3 text-center text-xs sm:text-sm font-semibold text-slate-600 border-l border-b border-slate-200 bg-slate-50 first:border-l-0"
                  >
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{day.charAt(0)}</span>
                  </div>
                ))}
              </div>
              
              {/* Calendar weeks */}
              {weeks.map((week) => {
                const weekKey = week[0].toISOString();
                return (
                  <div key={weekKey} className="grid" style={{ gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))' }}>
                    {week.map((day) => {
                      const isCurrentMonth = isSameMonth(day, currentDate);
                      const isToday = isSameDay(day, new Date());
                      const dayJobs = getJobsForDay(day);
                      
                      // Filter jobs based on selected employee if in personnel mode
                      const filteredDayJobs = viewMode === 'personnel' && selectedEmployee !== 'all'
                        ? dayJobs.filter(job => job.assigned_to?.includes(selectedEmployee))
                        : dayJobs;

                      // Get time off for this day (filter by selected employee if applicable)
                      const dayTimeOff = selectedEmployee !== 'all'
                        ? getTimeOffForDay(selectedEmployee, day)
                        : getTimeOffForDayAll(day);
                      const hasTimeOff = dayTimeOff.length > 0;

                      return (
                        <div
                          key={day.toISOString()}
                          className={`min-h-[120px] sm:min-h-[150px] border-t border-l border-slate-200 first:border-l-0 p-0.5 sm:p-1 ${
                            !isCurrentMonth ? 'bg-slate-100' : hasTimeOff ? 'bg-purple-50' : 'bg-white'
                          }`}
                          onClick={() => setSelectedDate(day)}
                          data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                        >
                          <div className={`text-xs sm:text-sm font-medium w-5 h-5 sm:w-7 sm:h-7 flex items-center justify-center rounded-full mb-0.5 sm:mb-1 ${
                            isToday 
                              ? 'bg-blue-500 text-white' 
                              : isCurrentMonth ? 'text-slate-700' : 'text-slate-400'
                          }`}>
                            {format(day, 'd')}
                          </div>

                          {/* Time Off indicator for this day */}
                          {hasTimeOff && (
                            <div className="mb-0.5 sm:mb-1">
                              {dayTimeOff.slice(0, 1).map((timeOff, idx) => {
                                const emp = employees.find(e => e.id === timeOff.user_id);
                                return (
                                  <div
                                    key={timeOff.id || idx}
                                    className="bg-purple-100 text-purple-700 rounded px-0.5 sm:px-1 py-0.5 text-[8px] sm:text-[10px] flex items-center gap-0.5 mb-0.5"
                                    title={`${emp?.name || 'Employee'} - Time Off: ${timeOff.reason || 'No reason'}`}
                                    data-testid={`timeoff-${timeOff.id || idx}`}
                                  >
                                    <Palmtree className="w-2 h-2 sm:w-2.5 sm:h-2.5 flex-shrink-0" />
                                    <span className="truncate hidden sm:inline">{emp?.name || 'Off'}</span>
                                    <span className="truncate sm:hidden">Off</span>
                                  </div>
                                );
                              })}
                              {dayTimeOff.length > 1 && (
                                <div className="text-[8px] sm:text-[10px] text-purple-600 dark:text-purple-300 px-0.5 sm:px-1">
                                  +{dayTimeOff.length - 1}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Jobs for this day - displayed horizontally (flex-wrap) */}
                          <div className="flex flex-wrap gap-0.5 sm:gap-1">
                            {viewMode === 'personnel' ? (
                              // Personnel mode: show time entries (actual worked hours)
                              (() => {
                                const dayTimeEntries = getTimeEntriesForDay(day);
                                
                                // Filter by selected employee if needed
                                const filteredEntries = selectedEmployee !== 'all'
                                  ? dayTimeEntries.filter(e => e.employee_id === selectedEmployee)
                                  : dayTimeEntries;
                                
                                const displayItems = filteredEntries.slice(0, 6);
                                const remainingCount = filteredEntries.length - 6;
                                
                                if (filteredEntries.length === 0) {
                                  // Show job assignments if no time entries (fallback)
                                  const personnelItems = [];
                                  filteredDayJobs.forEach((job) => {
                                    (job.assigned_to || []).forEach((empId, idx) => {
                                      if (selectedEmployee === 'all' || selectedEmployee === empId) {
                                        personnelItems.push({
                                          job,
                                          empId,
                                          empName: job.assigned_names?.[idx] || 'Unknown',
                                          isAssignment: true
                                        });
                                      }
                                    });
                                  });
                                  const assignmentDisplay = personnelItems.slice(0, 6);
                                  const assignmentRemaining = personnelItems.length - 6;
                                  
                                  return (
                                    <>
                                      {assignmentDisplay.map((item, idx) => {
                                        const colors = getEmployeeColor(item.empId);
                                        return (
                                          <div
                                            key={`${item.job.id}-${item.empId}`}
                                            className={`${colors.light} rounded px-0.5 sm:px-1 py-0.5 text-[8px] sm:text-[10px] cursor-pointer hover:opacity-80 transition-opacity truncate max-w-full opacity-60`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedJob(item.job);
                                            }}
                                            data-testid={`event-${item.job.id}-${item.empId}`}
                                            title={`${item.empName} - ${item.job.title} (Assigned, no time logged)`}
                                          >
                                            <span className="truncate">{item.empName}</span>
                                          </div>
                                        );
                                      })}
                                      {assignmentRemaining > 0 && (
                                        <div className="text-[8px] sm:text-[10px] text-muted-foreground px-0.5 sm:px-1">
                                          +{assignmentRemaining}
                                        </div>
                                      )}
                                    </>
                                  );
                                }
                                
                                return (
                                  <>
                                    {displayItems.map((entry) => {
                                      const colors = getEmployeeColor(entry.employee_id);
                                      return (
                                        <div
                                          key={entry.id}
                                          className={`${colors.light} rounded px-0.5 sm:px-1 py-0.5 text-[8px] sm:text-[10px] cursor-pointer hover:opacity-80 transition-opacity truncate max-w-full`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            // Find and show the associated job
                                            const job = jobs.find(j => j.id === entry.job_id);
                                            if (job) setSelectedJob(job);
                                          }}
                                          data-testid={`time-entry-${entry.id}`}
                                          title={`${entry.employee_name} - ${entry.job_title} - ${entry.hours}h (${entry.start_time} - ${entry.end_time})`}
                                        >
                                          <span className="truncate">{entry.employee_name} ({entry.hours}h)</span>
                                        </div>
                                      );
                                    })}
                                    {remainingCount > 0 && (
                                      <div className="text-[8px] sm:text-[10px] text-muted-foreground px-0.5 sm:px-1">
                                        +{remainingCount}
                                      </div>
                                    )}
                                  </>
                                );
                              })()
                            ) : (
                              // Jobs mode: show job titles
                              <>
                                {filteredDayJobs.slice(0, 5).map((job) => {
                                  const colors = jobStatusColors[job.status] || jobStatusColors.pending;
                                  return (
                                    <div
                                      key={job.id}
                                      className={`${colors.light} rounded px-0.5 sm:px-1 py-0.5 text-[8px] sm:text-[10px] cursor-pointer hover:opacity-80 transition-opacity truncate max-w-full`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedJob(job);
                                      }}
                                      data-testid={`event-${job.id}`}
                                      title={`${job.title} - ${format(parseISO(job.start_time), 'h:mm a')}`}
                                    >
                                      <span className="truncate">{job.title}</span>
                                    </div>
                                  );
                                })}
                                {filteredDayJobs.length > 5 && (
                                  <div className="text-[8px] sm:text-[10px] text-muted-foreground px-0.5 sm:px-1">
                                    +{filteredDayJobs.length - 5}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Job Detail Dialog */}
      <Dialog open={!!selectedJob} onOpenChange={() => setSelectedJob(null)}>
        <DialogContent data-testid="job-detail-dialog" className="max-w-[90vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg sm:text-xl">{selectedJob?.title}</DialogTitle>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-3 sm:space-y-4">
              <Badge className={`badge-status ${
                selectedJob.status === 'pending' ? 'status-pending' :
                selectedJob.status === 'in_progress' ? 'status-in_progress' :
                selectedJob.status === 'completed' ? 'status-completed' :
                'status-cancelled'
              }`}>
                {selectedJob.status.replace('_', ' ')}
              </Badge>

              {selectedJob.description && (
                <p className="text-muted-foreground">{selectedJob.description}</p>
              )}

              <div className="space-y-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">Start:</span>
                    <span>{format(parseISO(selectedJob.start_time), 'MM/dd/yyyy hh:mm a')}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">End:</span>
                    <span>{format(parseISO(selectedJob.end_time), 'MM/dd/yyyy hh:mm a')}</span>
                  </div>
                </div>

                {selectedJob.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedJob.location}</span>
                  </div>
                )}

                {selectedJob.customer_name && (
                  <div className="flex items-center gap-3">
                    <Building2 className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedJob.customer_name}</span>
                  </div>
                )}

                {selectedJob.assigned_names?.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">Assigned Personnel:</span>
                    </div>
                    <div className="ml-7 space-y-2">
                      {selectedJob.assigned_names.map((name, idx) => (
                        <div key={`${name}-${idx}`} className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs bg-primary text-primary-foreground">
                              {name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSelectedJob(null);
                    navigate(`/jobs/${selectedJob.id}`);
                  }}
                >
                  View Details
                </Button>
                {isAdmin && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      setSelectedJob(null);
                      navigate(`/jobs/${selectedJob.id}/edit`);
                    }}
                  >
                    Edit Job
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Date Detail Dialog */}
      <Dialog open={!!selectedDate} onOpenChange={() => setSelectedDate(null)}>
        <DialogContent className="max-w-lg" data-testid="date-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {selectedDate && format(selectedDate, 'EEEE, MMMM d, yyyy')}
            </DialogTitle>
          </DialogHeader>
          {selectedDate && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {/* Time Off Section */}
              {getTimeOffForDayAll(selectedDate).length > 0 && (
                <div className="space-y-2" data-testid="date-time-off-section">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <Palmtree className="w-4 h-4" />
                    Time Off
                  </h4>
                  {getTimeOffForDayAll(selectedDate).map((timeOff, idx) => {
                    const emp = employees.find(e => e.id === timeOff.user_id);
                    return (
                      <div
                        key={timeOff.id || idx}
                        className="p-3 rounded-lg bg-purple-100 dark:bg-purple-800/50 text-purple-800 dark:text-purple-200"
                        data-testid={`date-timeoff-${timeOff.id || idx}`}
                      >
                        <div className="font-medium">{emp?.name || 'Employee'}</div>
                        {timeOff.reason && (
                          <div className="text-sm opacity-75 mt-1">{timeOff.reason}</div>
                        )}
                        <div className="text-xs mt-1 opacity-60">
                          {format(parseISO(timeOff.start_date), 'MMM d')} - {format(parseISO(timeOff.end_date), 'MMM d, yyyy')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Jobs Section */}
              {getJobsForDay(selectedDate).length === 0 && getTimeOffForDayAll(selectedDate).length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No jobs or time off scheduled</p>
              ) : getJobsForDay(selectedDate).length > 0 && (
                <div className="space-y-2">
                  {getTimeOffForDayAll(selectedDate).length > 0 && (
                    <h4 className="text-sm font-semibold flex items-center gap-2">
                      <Briefcase className="w-4 h-4" />
                      Jobs
                    </h4>
                  )}
                  {getJobsForDay(selectedDate).map((job) => {
                    const colors = jobStatusColors[job.status] || jobStatusColors.pending;
                    return (
                      <div
                        key={job.id}
                        className={`p-4 rounded-lg cursor-pointer hover:shadow-md transition-all ${colors.light}`}
                        onClick={() => {
                          setSelectedDate(null);
                          setSelectedJob(job);
                        }}
                        data-testid={`date-job-${job.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${colors.bg}`} />
                          <span className="text-xs font-bold">
                            {format(parseISO(job.start_time), 'MM/dd/yyyy hh:mm a')} - {format(parseISO(job.end_time), 'MM/dd/yyyy hh:mm a')}
                          </span>
                        </div>
                        <div className="font-semibold mt-1">{job.title}</div>
                        {job.assigned_names?.length > 0 && (
                          <div className="text-sm opacity-75 mt-1">
                            {job.assigned_names.join(', ')}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {isAdmin && (
                <Button
                  className="w-full mt-4"
                  onClick={() => {
                    setSelectedDate(null);
                    navigate('/jobs/new', { state: { date: selectedDate } });
                  }}
                  data-testid="add-job-from-date-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Job
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sync Calendar Dialog */}
      <Dialog open={showSyncDialog} onOpenChange={setShowSyncDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Sync with Calendar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Subscribe to your schedule in Google Calendar, Outlook, or Apple Calendar using this URL.
              Changes will automatically sync (refresh time depends on your calendar app).
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={calendarFeedUrl}
                readOnly
                className="flex-1 px-3 py-2 border rounded-md text-sm bg-muted"
              />
              <Button onClick={copyToClipboard}>
                Copy
              </Button>
            </div>
            <div className="text-xs text-muted-foreground bg-blue-50 text-blue-800 p-3 rounded-md">
              <strong>Google Calendar:</strong> Click "+" next to "Other calendars" {'>'} "From URL".
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
